import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, Loader2, Calculator, Package, AlertTriangle, CheckCircle, ArrowRightLeft, RefreshCw, DollarSign, Edit3, ToggleLeft, ToggleRight, Pencil, Save, X } from 'lucide-react';
import { bomAPI } from '../../services/api';
import { toast } from 'sonner';
import ProductSearchInput from '../ui/ProductSearchInput';

const BOMDetailView = ({ bomId, onBack }) => {
  const [bom, setBom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [tab, setTab] = useState('tree');
  const [simulation, setSimulation] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [swaps, setSwaps] = useState({});
  const [rateOverrides, setRateOverrides] = useState({});
  const [availability, setAvailability] = useState(null);
  const [availQty, setAvailQty] = useState(1);
  const [availLoading, setAvailLoading] = useState(false);
  const [useFifo, setUseFifo] = useState(false);
  const [fifoRates, setFifoRates] = useState(null);
  const [fifoLoading, setFifoLoading] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editChildBomCost, setEditChildBomCost] = useState(0);
  const [editFgOtherCost, setEditFgOtherCost] = useState(0);
  const [editRemarks, setEditRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');

  useEffect(() => {
    bomAPI.getById(bomId).then(d => { setBom(d); setLoading(false); }).catch(() => setLoading(false));
  }, [bomId]);

  const toggleExpand = (seq) => {
    setExpandedItems(prev => ({ ...prev, [seq]: !prev[seq] }));
  };

  const expandAll = () => {
    if (!bom) return;
    const all = {};
    bom.items.forEach(item => { if (item.alternates?.length > 0) all[item.seq] = true; });
    setExpandedItems(all);
  };

  const collapseAll = () => setExpandedItems({});

  const runSimulation = async () => {
    try {
      setSimLoading(true);
      const res = await bomAPI.simulate(bomId, { item_swaps: swaps, rate_overrides: rateOverrides, use_fifo: useFifo });
      setSimulation(res);
    } catch (err) {
      console.error('Simulation failed:', err);
      toast.error('Simulation failed');
    } finally {
      setSimLoading(false);
    }
  };

  const loadFifoRates = async () => {
    try {
      setFifoLoading(true);
      const res = await bomAPI.getFIFORates(bomId);
      setFifoRates(res);
    } catch {
      toast.error('Failed to load FIFO rates');
    } finally {
      setFifoLoading(false);
    }
  };

  const toggleFifo = () => {
    const next = !useFifo;
    setUseFifo(next);
    if (next && !fifoRates) loadFifoRates();
  };

  const setRateOverride = (itemId, val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num < 0) {
      const next = { ...rateOverrides };
      delete next[itemId];
      setRateOverrides(next);
    } else {
      setRateOverrides(prev => ({ ...prev, [itemId]: num }));
    }
  };

  const checkAvailability = async () => {
    try {
      setAvailLoading(true);
      const res = await bomAPI.checkAvailability(bomId, availQty);
      setAvailability(res);
    } catch (err) {
      console.error('Availability check failed:', err);
    } finally {
      setAvailLoading(false);
    }
  };

  const handleSwap = (seq, altItemId) => {
    setSwaps(prev => {
      if (prev[seq] === altItemId) {
        const next = { ...prev };
        delete next[seq];
        return next;
      }
      return { ...prev, [seq]: altItemId };
    });
  };

  // BOM Edit functions
  const canEdit = ['Admin', 'Manager'].includes(user.role);

  const startEditing = () => {
    setEditItems((bom.items || []).map(item => ({
      ...item,
      alternates: item.alternates || [],
    })));
    setEditChildBomCost(bom.child_bom_other_cost || 0);
    setEditFgOtherCost(bom.fg_other_cost || 0);
    setEditRemarks(bom.remarks || '');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditItems([]);
  };

  const updateEditItem = (idx, field, value) => {
    setEditItems(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updated[idx].quantity) || 0;
        const rate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(updated[idx].rate) || 0;
        updated[idx].total_cost = Math.round(qty * rate * 100) / 100;
      }
      return updated;
    });
  };

  const editItemsTotal = editItems.reduce((sum, i) => sum + (Number(i.total_cost) || 0), 0);
  const editTotalFgCost = editItemsTotal + (Number(editChildBomCost) || 0) + (Number(editFgOtherCost) || 0);

  // Add / remove material rows in Edit mode (parity with BOMCreateForm)
  const addEditItem = () => {
    setEditItems(prev => [
      ...prev,
      {
        seq: String(prev.length + 1),
        item_id: '',
        item_description: '',
        quantity: 0,
        unit: 'Kg',
        rate: 0,
        total_cost: 0,
        is_primary: true,
        alternates: [],
      },
    ]);
  };

  const removeEditItem = (idx) => {
    if (editItems.length <= 1) return;  // BOM must keep at least one item
    setEditItems(prev => prev.filter((_, i) => i !== idx).map((it, i) => ({ ...it, seq: String(i + 1) })));
  };

  const applyProductToEditItem = (idx, product) => {
    if (!product) return;
    setEditItems(prev => {
      const updated = [...prev];
      const qty = Number(updated[idx]?.quantity) || 0;
      const rate = Number(product.cost_price || 0);
      updated[idx] = {
        ...updated[idx],
        item_id: product.item_code || product.sku || product.id,
        item_description: product.name,
        unit: product.unit || updated[idx].unit || 'Kg',
        rate,
        total_cost: Math.round(qty * rate * 100) / 100,
      };
      return updated;
    });
  };

  const clearEditItemProduct = (idx) => {
    setEditItems(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], item_id: '', item_description: '', unit: 'Kg', rate: 0, total_cost: 0 };
      return updated;
    });
  };

  const saveBOMEdits = async () => {
    try {
      // Guard against empty material rows added but never filled.
      // Surface exact row numbers so user can spot the unselected ProductSearchInput
      // (e.g. typed-but-not-clicked dropdown match) instead of guessing.
      const blankRows = editItems
        .map((it, i) => ({ i, ok: !!it.item_description && !!it.item_id }))
        .filter(r => !r.ok)
        .map(r => r.i + 1);
      if (blankRows.length > 0) {
        toast.error(
          `Row ${blankRows.join(', ')} — please CLICK a product from the search dropdown (typing alone doesn't select it). Or remove the empty row using the ✕ button.`,
          { duration: 7000 }
        );
        return;
      }
      setSaving(true);
      const cleanItems = editItems.map(item => ({
        seq: item.seq,
        item_id: item.item_id,
        item_description: item.item_description,
        quantity: parseFloat(item.quantity) || 0,
        unit: item.unit,
        rate: parseFloat(item.rate) || 0,
        total_cost: parseFloat(item.total_cost) || 0,
        is_primary: item.is_primary !== false,
        alternates: item.alternates || [],
      }));
      const updated = await bomAPI.update(bomId, {
        items: cleanItems,
        child_bom_other_cost: Number(editChildBomCost) || 0,
        fg_other_cost: Number(editFgOtherCost) || 0,
        remarks: editRemarks,
      });
      setBom(updated);
      setEditing(false);
      toast.success('BOM updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update BOM');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!bom) {
    return (
      <div className="p-5 lg:p-6">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <p className="text-sm text-gray-500">BOM not found</p>
      </div>
    );
  }

  const totalAlternates = bom.items.reduce((sum, it) => sum + (it.alternates?.length || 0), 0);

  return (
    <div className="p-5 lg:p-6">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to BOMs
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{bom.bom_name}</h1>
              <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-700">v{bom.version}</span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-gray-500 mt-1">
              <span className="font-mono font-semibold">{bom.bom_number}</span>
              <span>FG: <span className="font-semibold text-gray-800">{bom.fg_item_id}</span></span>
              <span>UOM: {bom.fg_uom}</span>
              <span>{bom.items.length} primary items</span>
              <span>{totalAlternates} alternates</span>
            </div>
          </div>
          <div className="text-right flex items-center gap-3">
            {canEdit && !editing && (
              <button onClick={startEditing} data-testid="edit-bom-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                <Pencil className="w-3 h-3" /> Edit BOM
              </button>
            )}
            <div>
              <p className="text-[10px] text-gray-500">Total FG Cost</p>
              <p className="text-xl font-bold" style={{ color: '#24549c' }}>
                Rs. {(editing ? editTotalFgCost : bom.total_fg_cost) ? Number(editing ? editTotalFgCost : bom.total_fg_cost).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
        {[
          { id: 'tree', label: 'BOM Tree', icon: Package },
          { id: 'simulate', label: 'Cost Simulator', icon: Calculator },
          { id: 'fifo', label: 'FIFO Pricing', icon: DollarSign },
          { id: 'availability', label: 'Material Availability', icon: AlertTriangle },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* BOM Tree */}
      {tab === 'tree' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-xs font-bold text-gray-900">Material Breakdown</h3>
            <div className="flex gap-2">
              {editing ? (
                <>
                  <button onClick={cancelEditing} className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">
                    <X className="w-3 h-3" /> Cancel
                  </button>
                  <button onClick={saveBOMEdits} disabled={saving} data-testid="save-bom-btn"
                    className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold rounded-lg text-white disabled:opacity-50" style={{ backgroundColor: '#059669' }}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save BOM
                  </button>
                </>
              ) : (
                <>
                  <button onClick={expandAll} className="text-[10px] text-blue-600 hover:underline">Expand All</button>
                  <button onClick={collapseAll} className="text-[10px] text-gray-500 hover:underline">Collapse All</button>
                </>
              )}
            </div>
          </div>

          {/* Edit Mode */}
          {editing ? (
            <div>
              {/* Editable table */}
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500 w-10">#</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500">Item ID</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500">Description</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-500 w-20">Qty</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-500 w-16">Unit</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-500 w-20">Rate</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-500 w-24">Total</th>
                    <th className="text-center px-2 py-2 font-semibold text-gray-500 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {editItems.map((item, idx) => {
                    const rowUnselected = !item.item_description || !item.item_id;
                    return (
                    <tr key={idx} data-testid={`edit-bom-row-${idx}`} className={rowUnselected ? 'bg-amber-50/50' : ''}>
                      <td className="px-3 py-2 text-gray-400 font-mono font-bold">{item.seq}</td>
                      <td className="px-3 py-2 font-mono text-gray-600">{item.item_id || <span className="text-amber-600 font-bold">— pick —</span>}</td>
                      <td className="px-3 py-2 text-gray-700 min-w-[220px]">
                        {item.item_description ? (
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate">{item.item_description}</span>
                            <button onClick={() => clearEditItemProduct(idx)} data-testid={`edit-bom-clear-product-${idx}`}
                              title="Change product" className="text-[10px] text-blue-600 hover:underline flex-shrink-0">change</button>
                          </div>
                        ) : (
                          <div>
                            <ProductSearchInput
                              testId={`edit-bom-product-search-${idx}`}
                              placeholder="Search product by name or SKU…"
                              onSelect={(p) => applyProductToEditItem(idx, p)}
                            />
                            <p className="text-[10px] text-amber-700 mt-1 font-medium">⚠ Click a result from the dropdown — typing alone does NOT select.</p>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" step="0.001" value={item.quantity ?? ''} onChange={e => updateEditItem(idx, 'quantity', e.target.value)}
                          data-testid={`edit-bom-qty-${idx}`}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-200" />
                      </td>
                      <td className="px-3 py-2 text-gray-500">{item.unit}</td>
                      <td className="px-3 py-2">
                        <input type="number" step="0.01" value={item.rate ?? ''} onChange={e => updateEditItem(idx, 'rate', e.target.value)}
                          data-testid={`edit-bom-rate-${idx}`}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-200" />
                      </td>
                      <td className="px-3 py-2 text-right font-semibold font-mono text-gray-900">{(item.total_cost || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => removeEditItem(idx)}
                          disabled={editItems.length <= 1}
                          data-testid={`edit-bom-remove-row-${idx}`}
                          title={editItems.length <= 1 ? 'A BOM must keep at least one material row' : 'Remove this row'}
                          className="text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed">
                          <X className="w-3.5 h-3.5 inline" />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={8} className="px-3 py-2 border-t border-gray-100">
                      <button
                        onClick={addEditItem}
                        data-testid="edit-bom-add-row-btn"
                        className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700">
                        <Pencil className="w-3 h-3" /> Add material row
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              {/* Cost summary for edit mode */}
              <div className="px-4 py-3 border-t border-gray-100 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Materials Total</span>
                  <span className="font-semibold font-mono">Rs. {editItemsTotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-500">Child BOM Other Cost</span>
                  <input type="number" step="0.01" value={editChildBomCost || ''} onChange={e => setEditChildBomCost(Number(e.target.value))}
                    data-testid="edit-child-bom-cost"
                    className="w-24 px-2 py-1 text-xs border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-500">FG Other Cost</span>
                  <input type="number" step="0.01" value={editFgOtherCost || ''} onChange={e => setEditFgOtherCost(Number(e.target.value))}
                    data-testid="edit-fg-other-cost"
                    className="w-24 px-2 py-1 text-xs border border-gray-200 rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-bold text-gray-800">Total FG Cost</span>
                  <span className="font-bold font-mono" style={{ color: '#24549c' }}>Rs. {editTotalFgCost.toFixed(2)}</span>
                </div>
                <div className="pt-1">
                  <span className="text-gray-500 text-[10px]">Remarks</span>
                  <input value={editRemarks} onChange={e => setEditRemarks(e.target.value)}
                    data-testid="edit-bom-remarks"
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded mt-1 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="Optional remarks" />
                </div>
              </div>
            </div>
          ) : (
            /* Read-only mode */
            <div className="divide-y divide-gray-50">
              {bom.items.map(item => (
                <div key={item.seq} data-testid={`bom-item-${item.seq}`}>
                  {/* Primary Item */}
                  <div className="flex items-center px-4 py-3 hover:bg-gray-50/50 cursor-pointer" onClick={() => item.alternates?.length > 0 && toggleExpand(item.seq)}>
                    <div className="w-6 flex-shrink-0">
                      {item.alternates?.length > 0 ? (
                        expandedItems[item.seq]
                          ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                          : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                      ) : <div className="w-3.5" />}
                    </div>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0" style={{ backgroundColor: '#24549c15' }}>
                      <span className="text-[10px] font-bold" style={{ color: '#24549c' }}>#{item.seq}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-semibold text-gray-500">{item.item_id}</span>
                        <span className="text-xs font-semibold text-gray-900 truncate">{item.item_description}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-[11px] flex-shrink-0">
                      <span className="text-gray-600 w-20 text-right">{item.quantity} {item.unit}</span>
                      <span className="text-gray-600 w-20 text-right font-mono">{item.rate != null ? `Rs.${item.rate}` : '-'}</span>
                      <span className="font-semibold text-gray-900 w-24 text-right font-mono">{item.total_cost != null ? `Rs.${Number(item.total_cost).toLocaleString('en-IN')}` : '-'}</span>
                      {item.alternates?.length > 0 && (
                        <span className="px-2 py-0.5 text-[9px] font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          {item.alternates.length} alt{item.alternates.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Alternates */}
                  {expandedItems[item.seq] && item.alternates?.map(alt => (
                    <div key={alt.seq} className="flex items-center px-4 py-2.5 bg-gray-50/50 border-l-2 border-amber-200 ml-6">
                      <div className="w-6 flex-shrink-0" />
                      <ArrowRightLeft className="w-3 h-3 text-amber-500 mr-3 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-amber-600">{alt.item_id}</span>
                          <span className="text-[11px] text-gray-600 truncate">{alt.item_description}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-[10px] flex-shrink-0">
                        <span className="text-gray-500 w-20 text-right">{alt.quantity} {alt.unit}</span>
                        <span className="text-gray-400 w-20 text-right">-</span>
                        <span className="text-gray-400 w-24 text-right">-</span>
                        <span className="w-14" />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cost Simulator */}
      {tab === 'simulate' && (
        <div className="space-y-4">
          {/* FIFO Toggle + Rate Override Controls */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-900">Pricing Mode</h3>
              <button onClick={toggleFifo} data-testid="fifo-toggle" className="flex items-center gap-2 text-xs font-medium">
                {useFifo
                  ? <><ToggleRight className="w-5 h-5 text-emerald-600" /><span className="text-emerald-700">FIFO Pricing Active</span></>
                  : <><ToggleLeft className="w-5 h-5 text-gray-400" /><span className="text-gray-500">BOM Static Rates</span></>}
              </button>
            </div>

            {useFifo && fifoRates && (
              <div className="mb-4 p-3 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-[11px] font-semibold text-emerald-700">FIFO Rate Comparison</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div className="p-2 bg-white rounded-lg"><p className="text-gray-500">BOM Cost</p><p className="font-bold text-gray-900">Rs. {fifoRates.total_bom_cost?.toLocaleString('en-IN')}</p></div>
                  <div className="p-2 bg-white rounded-lg"><p className="text-gray-500">FIFO Cost</p><p className="font-bold text-emerald-700">Rs. {fifoRates.total_fifo_cost?.toLocaleString('en-IN')}</p></div>
                  <div className="p-2 bg-white rounded-lg"><p className="text-gray-500">Difference</p>
                    <p className={`font-bold ${fifoRates.difference > 0 ? 'text-red-600' : fifoRates.difference < 0 ? 'text-emerald-600' : 'text-gray-600'}`}>
                      {fifoRates.difference > 0 ? '+' : ''}Rs. {fifoRates.difference?.toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {useFifo && fifoLoading && (
              <div className="flex items-center gap-2 py-3 text-xs text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading FIFO rates...</div>
            )}

            {/* Editable Rates Table */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Edit3 className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[11px] font-semibold text-gray-700">Override Rates (click any rate to edit)</span>
              </div>
              <div className="max-h-[250px] overflow-y-auto border rounded-lg">
                <table className="w-full text-[11px]">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-500">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-500">Material</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500">BOM Rate</th>
                      {useFifo && <th className="text-right px-3 py-2 font-semibold text-gray-500">FIFO Rate</th>}
                      <th className="text-right px-3 py-2 font-semibold text-gray-500">Override</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {bom.items.map(item => {
                      const fr = fifoRates?.fifo_rates?.[item.item_id];
                      const hasOverride = item.item_id in rateOverrides;
                      return (
                        <tr key={item.seq} className={hasOverride ? 'bg-blue-50/30' : ''} data-testid={`rate-row-${item.seq}`}>
                          <td className="px-3 py-2 font-bold text-gray-400">#{item.seq}</td>
                          <td className="px-3 py-2">
                            <span className="font-mono text-gray-500 text-[10px]">{item.item_id}</span>
                            <span className="ml-1 text-gray-700 truncate">{(item.item_description || '').slice(0, 30)}</span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{item.rate != null ? item.rate : '-'}</td>
                          {useFifo && (
                            <td className="px-3 py-2 text-right font-mono">
                              {fr != null ? <span className="text-emerald-600">{fr}</span> : <span className="text-gray-300">N/A</span>}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right">
                            {editingRate === item.item_id ? (
                              <input autoFocus type="number" step="0.01"
                                className="w-20 px-2 py-1 text-right text-[11px] border rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
                                defaultValue={rateOverrides[item.item_id] ?? ''}
                                onBlur={e => { setRateOverride(item.item_id, e.target.value); setEditingRate(null); }}
                                onKeyDown={e => { if (e.key === 'Enter') { setRateOverride(item.item_id, e.target.value); setEditingRate(null); } }}
                                data-testid={`rate-input-${item.seq}`}
                              />
                            ) : (
                              <button onClick={() => setEditingRate(item.item_id)} className="text-right font-mono hover:text-blue-600 transition-colors"
                                data-testid={`rate-edit-${item.seq}`}>
                                {hasOverride ? <span className="text-blue-600 font-semibold">{rateOverrides[item.item_id]}</span> : <span className="text-gray-300">—</span>}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Object.keys(rateOverrides).length > 0 && (
                <button onClick={() => setRateOverrides({})} className="mt-2 text-[10px] text-red-500 hover:text-red-700">Clear all overrides</button>
              )}
            </div>

            {/* Swap Materials */}
            {bom.items.filter(it => it.alternates?.length > 0).length > 0 && (
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                  <ArrowRightLeft className="w-3.5 h-3.5 text-amber-500" /> Swap Alternate Materials
                </h4>
                <div className="space-y-3">
                  {bom.items.filter(it => it.alternates?.length > 0).map(item => (
                    <div key={item.seq} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold text-gray-500">#{item.seq}</span>
                        <span className="text-xs font-semibold text-gray-800">{item.item_description}</span>
                        <span className="text-[10px] text-gray-400 font-mono">({item.item_id})</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <button onClick={() => { const next = { ...swaps }; delete next[item.seq]; setSwaps(next); }}
                          className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg border transition-all ${!swaps[item.seq] ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                          {item.item_id} (Primary)
                        </button>
                        {item.alternates.map(alt => (
                          <button key={alt.seq} onClick={() => handleSwap(item.seq, alt.item_id)}
                            className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg border transition-all ${swaps[item.seq] === alt.item_id ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                            {alt.item_id}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={runSimulation} disabled={simLoading} data-testid="run-simulation-btn"
              className="px-5 py-2 text-xs font-semibold text-white rounded-lg flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {simLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calculator className="w-3 h-3" />}
              Run Simulation {useFifo ? '(FIFO)' : ''}
            </button>
          </div>

          {/* Simulation Results */}
          {simulation && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-bold text-gray-900 mb-3">Simulation Results</h3>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-gray-500">Original Cost</p>
                  <p className="text-lg font-bold text-gray-900">Rs. {Number(simulation.original_cost).toLocaleString('en-IN')}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-[10px] text-gray-500">Simulated Cost</p>
                  <p className="text-lg font-bold" style={{ color: '#24549c' }}>Rs. {Number(simulation.simulated_cost).toLocaleString('en-IN')}</p>
                </div>
                <div className={`p-3 rounded-lg ${simulation.difference > 0 ? 'bg-red-50' : simulation.difference < 0 ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                  <p className="text-[10px] text-gray-500">Difference</p>
                  <p className={`text-lg font-bold ${simulation.difference > 0 ? 'text-red-600' : simulation.difference < 0 ? 'text-emerald-600' : 'text-gray-600'}`}>
                    {simulation.difference > 0 ? '+' : ''}{Number(simulation.difference).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
              <div className="space-y-1.5">
                {simulation.items.map(it => (
                  <div key={it.seq} className={`flex items-center justify-between px-3 py-2 rounded-lg text-[11px] ${it.swapped ? 'bg-amber-50 border border-amber-100' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-500">#{it.seq}</span>
                      <span className="font-mono text-gray-600">{it.item_id}</span>
                      <span className="text-gray-800 truncate max-w-[200px]">{it.item_description}</span>
                      {it.swapped && (
                        <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-amber-100 text-amber-700 rounded">SWAPPED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span>{it.quantity} {it.unit}</span>
                      <span className="font-mono">@{it.rate}</span>
                      <span className="font-semibold font-mono">Rs.{Number(it.total_cost).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FIFO Pricing Tab */}
      {tab === 'fifo' && <FIFOPricingTab bom={bom} bomId={bomId} />}

      {/* Material Availability */}
      {tab === 'availability' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-bold text-gray-900 mb-3">Check Material Availability</h3>
            <div className="flex items-center gap-3">
              <div>
                <label className="text-[11px] font-medium text-gray-600 mb-1 block">Production Quantity</label>
                <input type="number" value={availQty} onChange={e => setAvailQty(Math.max(1, parseInt(e.target.value) || 1))}
                  data-testid="avail-qty-input" min={1}
                  className="w-24 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <button onClick={checkAvailability} disabled={availLoading} data-testid="check-avail-btn"
                className="mt-5 px-5 py-2 text-xs font-semibold text-white rounded-lg flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                {availLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Check
              </button>
            </div>
          </div>

          {availability && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className={`px-4 py-3 border-b ${availability.all_materials_available ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
                <div className="flex items-center gap-2">
                  {availability.all_materials_available
                    ? <><CheckCircle className="w-4 h-4 text-emerald-600" /><span className="text-xs font-semibold text-emerald-700">All materials available for {availability.production_qty} units</span></>
                    : <><AlertTriangle className="w-4 h-4 text-amber-600" /><span className="text-xs font-semibold text-amber-700">Some materials have insufficient stock</span></>
                  }
                </div>
              </div>
              <div className="divide-y divide-gray-50">
                {availability.items.map(it => (
                  <div key={it.seq} className={`px-4 py-3 ${!it.sufficient ? 'bg-red-50/30' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {it.sufficient
                          ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                          : <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                        <span className="text-[11px] font-mono text-gray-500">{it.item_id}</span>
                        <span className="text-[11px] text-gray-800 font-semibold">{it.item_description}</span>
                      </div>
                      <div className="flex items-center gap-4 text-[11px]">
                        <span className="text-gray-500">Need: <strong>{it.required} {it.unit}</strong></span>
                        <span className={it.sufficient ? 'text-emerald-600' : 'text-red-600'}>Stock: <strong>{it.in_stock}</strong></span>
                        {!it.sufficient && <span className="text-red-600 font-semibold">Deficit: {it.deficit}</span>}
                      </div>
                    </div>
                    {!it.sufficient && it.alternates_in_stock?.some(a => a.sufficient) && (
                      <div className="mt-2 ml-6 p-2 bg-emerald-50 rounded-lg">
                        <p className="text-[10px] text-emerald-700 font-semibold mb-1">Available alternates in stock:</p>
                        {it.alternates_in_stock.filter(a => a.sufficient).map(a => (
                          <p key={a.item_id} className="text-[10px] text-emerald-600">
                            {a.item_id} — {a.item_description} (Stock: {a.in_stock})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FIFOPricingTab = ({ bom, bomId }) => {
  const [fifo, setFifo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bomAPI.getFIFORates(bomId)
      .then(setFifo)
      .catch(() => toast.error('Failed to load FIFO rates'))
      .finally(() => setLoading(false));
  }, [bomId]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!fifo) return <p className="text-center py-16 text-gray-400 text-sm">Failed to load FIFO data</p>;

  const itemsWithFifo = fifo.comparison.filter(c => c.has_fifo).length;

  return (
    <div className="space-y-4" data-testid="fifo-pricing-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-[10px] text-gray-500 mb-1">BOM Cost</p>
          <p className="text-lg font-bold text-gray-900">Rs. {fifo.total_bom_cost?.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-[10px] text-gray-500 mb-1">FIFO Cost</p>
          <p className="text-lg font-bold text-emerald-700">Rs. {fifo.total_fifo_cost?.toLocaleString('en-IN')}</p>
        </div>
        <div className={`rounded-xl border p-4 ${fifo.difference > 0 ? 'bg-red-50 border-red-100' : fifo.difference < 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-gray-100'}`}>
          <p className="text-[10px] text-gray-500 mb-1">Difference</p>
          <p className={`text-lg font-bold ${fifo.difference > 0 ? 'text-red-600' : fifo.difference < 0 ? 'text-emerald-600' : 'text-gray-600'}`}>
            {fifo.difference > 0 ? '+' : ''}Rs. {fifo.difference?.toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-[10px] text-gray-500 mb-1">FIFO Coverage</p>
          <p className="text-lg font-bold text-blue-700">{itemsWithFifo}/{fifo.comparison.length}</p>
          <p className="text-[10px] text-gray-400">materials with stock prices</p>
        </div>
      </div>

      {/* Detailed Comparison Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-xs font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-3.5 h-3.5 text-emerald-600" /> Rate Comparison — BOM vs FIFO (First-In-First-Out)
          </h3>
          <span className="text-[10px] text-gray-400">FIFO uses earliest available stock purchase price</span>
        </div>
        <table className="w-full text-[11px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-500">#</th>
              <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Material</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-500">Qty</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-500">BOM Rate</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-500">FIFO Rate</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-500">BOM Cost</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-500">FIFO Cost</th>
              <th className="text-right px-4 py-2.5 font-semibold text-gray-500">Diff</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {fifo.comparison.map((c, i) => {
              const diff = c.fifo_cost - c.bom_cost;
              return (
                <tr key={i} className={c.has_fifo ? '' : 'bg-gray-50/30'} data-testid={`fifo-row-${i}`}>
                  <td className="px-4 py-2 font-bold text-gray-400">#{c.seq}</td>
                  <td className="px-4 py-2">
                    <span className="font-mono text-[10px] text-gray-500">{c.item_id}</span>
                    <span className="ml-1.5 text-gray-700">{(c.item_description || '').slice(0, 35)}</span>
                  </td>
                  <td className="px-4 py-2 text-right">{c.quantity} {c.unit}</td>
                  <td className="px-4 py-2 text-right font-mono">{c.bom_rate || '-'}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {c.has_fifo ? <span className="text-emerald-600 font-semibold">{c.fifo_rate}</span> : <span className="text-gray-300">N/A</span>}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{c.bom_cost?.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{c.fifo_cost?.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {diff !== 0 ? (
                      <span className={diff > 0 ? 'text-red-500' : 'text-emerald-500'}>{diff > 0 ? '+' : ''}{diff.toFixed(2)}</span>
                    ) : <span className="text-gray-300">0</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BOMDetailView;
